import { useState, useEffect, useCallback, Fragment } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Plus, ChevronDown, ChevronRight, Search, ListChecks, FileText, Trash2 } from 'lucide-react';
import { BACKEND_URL } from '../config/api';
import OnboardingIntake from './OnboardingIntake';

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } });
const API = `${BACKEND_URL}/api/v2/super-admin/onboarding`;

const timeAgo = (iso) => {
  if (!iso) return '';
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 3600) return `${Math.max(1, Math.round(s / 60))}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};

const StepDots = ({ steps, size = 'md' }) => (
  <div className="flex items-center gap-1" title={steps.map(s => `${s.done ? '✓' : '○'} ${s.label}`).join('\n')}>
    {steps.map((s, i) => (
      <Fragment key={s.key}>
        <div
          data-testid={`step-dot-${s.key}`}
          title={`${s.label}${s.done ? ' ✓' : ''}`}
          className={`rounded-full ${size === 'md' ? 'w-2.5 h-2.5' : 'w-2 h-2'} ${
            s.done ? 'bg-blue-600' : 'bg-gray-200'
          }`}
        />
        {i < steps.length - 1 && <div className={`h-px w-2 ${steps[i + 1].done ? 'bg-blue-400' : 'bg-gray-200'}`} />}
      </Fragment>
    ))}
  </div>
);

const PayChips = ({ c }) => (
  <span className="inline-flex gap-1">
    {c.stripeActive && <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700 ${c.provider === 'stripe' ? 'ring-1 ring-blue-300' : ''}`}>Stripe</span>}
    {c.ziinaConnected && <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-50 text-violet-700 ${c.provider === 'ziina' ? 'ring-1 ring-violet-300' : ''}`}>Ziina</span>}
    {!c.stripeActive && !c.ziinaConnected && <span className="text-[10px] text-gray-400">no processor</span>}
  </span>
);

export default function SuperAdminOnboardingTab({ onOpenTickets }) {
  const [view, setView] = useState('pipeline');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [stepFilter, setStepFilter] = useState('all');
  const [liveFilter, setLiveFilter] = useState('all');
  const [activityFilter, setActivityFilter] = useState('90');
  const [regionFilter, setRegionFilter] = useState('all');
  const [expanded, setExpanded] = useState(null);
  const [newTask, setNewTask] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(API, auth());
      setData(r.data);
    } catch {
      toast.error('Could not load the onboarding pipeline');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addTask = async (orgId) => {
    if (!newTask.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/${orgId}/tasks`, { text: newTask.trim() }, auth());
      setNewTask('');
      await load();
    } catch { toast.error('Could not add task'); } finally { setBusy(false); }
  };

  const toggleTask = async (task) => {
    try {
      await axios.patch(`${API}/tasks/${task.id}`, { done: !task.done }, auth());
      await load();
    } catch { toast.error('Could not update task'); }
  };

  const removeTask = async (task) => {
    try {
      await axios.delete(`${API}/tasks/${task.id}`, auth());
      await load();
    } catch { toast.error('Could not delete task'); }
  };

  const signOffStep = async (orgId, stepKey) => {
    try {
      await axios.post(`${API}/${orgId}/steps/${stepKey}/signoff`, {}, auth());
      toast.success('Step signed off');
      await load();
    } catch { toast.error('Could not sign off the step'); }
  };

  const undoSignOff = async (orgId, stepKey) => {
    try {
      await axios.delete(`${API}/${orgId}/steps/${stepKey}/signoff`, auth());
      await load();
    } catch { toast.error('Could not undo the sign-off'); }
  };

  if (view === 'docs') {
    return (
      <div>
        <ViewToggle view={view} setView={setView} />
        <OnboardingIntake />
      </div>
    );
  }

  // Region options: admins tagged with regions only see those; untagged (owner) sees both
  const REGION_COUNTRIES = { UAE: ['UAE', 'AE'], USA: ['USA', 'US'] };
  const adminRegions = data?.adminRegions?.length ? data.adminRegions : ['UAE', 'USA'];

  const companies = (data?.companies || [])
    .filter(c => !q || c.name.toLowerCase().includes(q.toLowerCase()) || (c.slug || '').includes(q.toLowerCase()))
    .filter(c => stepFilter === 'all' || c.currentStep === stepFilter)
    .filter(c => liveFilter === 'all' || (liveFilter === 'live' ? c.isLive : !c.isLive))
    .filter(c => regionFilter === 'all' || (REGION_COUNTRIES[regionFilter] || []).includes((c.country || '').toUpperCase()))
    .filter(c => activityFilter === 'all' || (c.lastActivityAt && (Date.now() - new Date(c.lastActivityAt).getTime()) <= Number(activityFilter) * 86400000));

  return (
    <div className="space-y-4" data-testid="onboarding-pipeline">
      <ViewToggle view={view} setView={setView} />

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-sm p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-gray-300 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="onboarding-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search companies…"
            className="w-full h-9 pl-9 pr-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
          />
        </div>
        <select
          data-testid="onboarding-step-filter"
          value={stepFilter}
          onChange={(e) => setStepFilter(e.target.value)}
          className="h-9 px-3 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none"
        >
          <option value="all">All steps</option>
          {(data?.stepDefs || []).map(s => <option key={s.key} value={s.label}>Stuck at: {s.label}</option>)}
        </select>
        {adminRegions.length > 1 && (
          <select
            data-testid="onboarding-region-filter"
            value={regionFilter}
            onChange={(e) => setRegionFilter(e.target.value)}
            className="h-9 px-3 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none"
          >
            <option value="all">Both regions</option>
            {adminRegions.map(r => <option key={r} value={r}>{r} only</option>)}
          </select>
        )}
        <select
          data-testid="onboarding-live-filter"
          value={liveFilter}
          onChange={(e) => setLiveFilter(e.target.value)}
          className="h-9 px-3 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none"
        >
          <option value="all">All companies</option>
          <option value="live">Live only</option>
          <option value="inprogress">In progress</option>
        </select>
        <select
          data-testid="onboarding-activity-filter"
          value={activityFilter}
          onChange={(e) => setActivityFilter(e.target.value)}
          className="h-9 px-3 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none"
        >
          <option value="all">Any activity</option>
          <option value="30">Active last month</option>
          <option value="90">Active last 3 months</option>
          <option value="180">Active last 6 months</option>
          <option value="365">Active last 12 months</option>
        </select>
        <span className="text-xs text-gray-400 ml-auto">{companies.length} compan{companies.length === 1 ? 'y' : 'ies'}</span>
      </div>

      {loading ? (
        <div className="bg-white rounded-2xl shadow-sm p-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-300" /></div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm divide-y divide-gray-100">
          {companies.length === 0 && <p className="p-8 text-sm text-gray-400 text-center">No companies match.</p>}
          {companies.map((c) => (
            <div key={c.id}>
              <button
                data-testid={`onboarding-row-${c.slug}`}
                onClick={() => setExpanded(expanded === c.id ? null : c.id)}
                className="w-full flex items-center gap-4 px-5 py-3.5 text-left hover:bg-gray-50/60 transition-colors"
              >
                {expanded === c.id ? <ChevronDown className="w-4 h-4 text-gray-300 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />}
                <div className="w-52 flex-shrink-0 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{c.name}</p>
                  <p className="text-[11px] text-gray-400 font-mono truncate">{c.slug} {c.country ? `· ${c.country}` : ''}</p>
                </div>
                <StepDots steps={c.steps} />
                <span className={`text-xs font-medium flex-shrink-0 ${c.isLive ? 'text-blue-600' : 'text-gray-600'}`} data-testid={`onboarding-current-${c.slug}`}>
                  {c.isLive ? (c.currentStep === 'Live' ? '● Live' : `● Live — ${c.currentStep} pending`) : c.currentStep} <span className="text-gray-300">· {c.progress}/{c.total}</span>
                </span>
                {c.openSupportTickets > 0 && (
                  <button
                    data-testid={`onboarding-ticket-badge-${c.slug}`}
                    onClick={(e) => { e.stopPropagation(); onOpenTickets && onOpenTickets(); }}
                    className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-50 text-red-600 border border-red-100 hover:bg-red-100 transition-colors"
                    title="Open support tickets for this company — click to view"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                    {c.openSupportTickets} support ticket{c.openSupportTickets !== 1 ? 's' : ''}
                  </button>
                )}
                <PayChips c={c} />
                {c.openTasks > 0 && (
                  <span className="px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 text-blue-700 flex-shrink-0">{c.openTasks} task{c.openTasks > 1 ? 's' : ''}</span>
                )}
                <span className="text-[11px] text-gray-400 ml-auto truncate max-w-[220px] text-right">
                  {c.lastActivity ? `${c.lastActivity.by} ${c.lastActivity.action} "${c.lastActivity.text}" · ${timeAgo(c.lastActivity.at)}` : ''}
                </span>
              </button>

              {expanded === c.id && (
                <div className="px-5 pb-5 pt-1 bg-gray-50/50" data-testid={`onboarding-detail-${c.slug}`}>
                  <div className="grid md:grid-cols-2 gap-5">
                    {/* Steps detail */}
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-gray-400 mb-2">Onboarding steps</p>
                      <div className="space-y-1">
                        {c.steps.map(s => (
                          <div key={s.key} className="flex items-center gap-2 text-sm group/step" data-testid={`onboarding-step-row-${c.slug}-${s.key}`}>
                            <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] ${s.done ? 'bg-blue-600 text-white' : 'border border-gray-300 text-transparent'}`}>✓</span>
                            <span className={s.done ? 'text-gray-700' : 'text-gray-400'}>{s.label}</span>
                            {s.signedOff ? (
                              <span className="text-[10px] text-amber-600 bg-amber-50 rounded px-1.5 py-0.5 flex items-center gap-1" data-testid={`onboarding-signoff-badge-${c.slug}-${s.key}`}>
                                signed off · {s.signedOff.by} · {timeAgo(s.signedOff.at)}
                                <button
                                  data-testid={`onboarding-signoff-undo-${c.slug}-${s.key}`}
                                  onClick={() => undoSignOff(c.id, s.key)}
                                  className="underline underline-offset-2 hover:text-amber-800"
                                  title="Undo sign-off"
                                >undo</button>
                              </span>
                            ) : !s.done && (
                              <button
                                data-testid={`onboarding-signoff-btn-${c.slug}-${s.key}`}
                                onClick={() => signOffStep(c.id, s.key)}
                                className="opacity-0 group-hover/step:opacity-100 focus:opacity-100 focus-visible:opacity-100 text-[10px] text-blue-600 hover:text-blue-800 underline underline-offset-2 transition-opacity"
                                title="Mark this step complete on the company's behalf"
                              >
                                Sign off
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <p className="text-[11px] text-gray-400 mt-3">
                        Extras: {['waivers', 'marketplace', 'website'].filter(k => c.extras?.[k]).join(' · ') || 'none yet'}
                      </p>
                      {c.waiverDecision && (
                        <div className="mt-2 bg-amber-50 rounded-lg px-2.5 py-2" data-testid={`onboarding-waiver-decision-${c.slug}`}>
                          <p className="text-[11px] font-semibold text-amber-800">
                            No-waiver sign-off · {c.waiverDecision.decidedBy}
                            {c.waiverDecision.decidedByEmail ? ` (${c.waiverDecision.decidedByEmail})` : ''}
                            {' · '}{c.waiverDecision.decidedAt ? new Date(c.waiverDecision.decidedAt).toLocaleString() : ''}
                          </p>
                          <p className="text-[10.5px] text-amber-700 mt-0.5 leading-snug">
                            "{c.waiverDecision.acknowledgment}"
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Tasks */}
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-gray-400 mb-2">Team to-dos</p>
                      <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                        {c.tasks.length === 0 && <p className="text-xs text-gray-400">No tasks yet — add what needs doing.</p>}
                        {c.tasks.map(t => (
                          <div key={t.id} className="flex items-start gap-2 group" data-testid={`onboarding-task-${t.id}`}>
                            <input
                              type="checkbox"
                              checked={t.done}
                              onChange={() => toggleTask(t)}
                              className="mt-0.5 cursor-pointer"
                              data-testid={`onboarding-task-toggle-${t.id}`}
                            />
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm leading-snug ${t.done ? 'line-through text-gray-400' : 'text-gray-800'}`}>{t.text}</p>
                              <p className="text-[10px] text-gray-400">
                                {t.createdBy} · {timeAgo(t.createdAt)}
                                {t.done && t.doneBy ? ` — ✓ ${t.doneBy} ${timeAgo(t.doneAt)}` : ''}
                              </p>
                            </div>
                            <button onClick={() => removeTask(t)} className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 transition-all" data-testid={`onboarding-task-delete-${t.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2 mt-3">
                        <input
                          data-testid="onboarding-new-task-input"
                          value={newTask}
                          onChange={(e) => setNewTask(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && addTask(c.id)}
                          placeholder="Add a task — e.g. collect waiver text"
                          className="flex-1 h-8 px-3 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-blue-400"
                        />
                        <button
                          data-testid="onboarding-add-task-btn"
                          onClick={() => addTask(c.id)}
                          disabled={busy}
                          className="h-8 px-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium flex items-center gap-1 disabled:opacity-60"
                        >
                          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Add
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const ViewToggle = ({ view, setView }) => (
  <div className="flex items-center gap-1 bg-white rounded-xl shadow-sm p-1 w-fit" data-testid="onboarding-view-toggle">
    {[['pipeline', 'Pipeline', ListChecks], ['docs', 'Intake docs', FileText]].map(([id, label, Icon]) => (
      <button
        key={id}
        data-testid={`onboarding-view-${id}`}
        onClick={() => setView(id)}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${
          view === id ? 'bg-blue-600 text-white' : 'text-gray-500 hover:text-gray-900'
        }`}
      >
        <Icon className="w-3.5 h-3.5" /> {label}
      </button>
    ))}
  </div>
);
